import { ParsedUrlQuery } from 'querystring'

import { useCallback, useEffect } from 'react'

import { GetStaticPropsContext } from 'next'
import { ArticleJsonLd } from 'next-seo'
import styled from 'styled-components'

import { FlexContainer, HeadTag } from '@/components'
import {
  AboutContainer,
  AuthorContainer,
  BlogPostHeader,
  ContentContainer,
  RecommendedArticlesContainer,
} from '@/containers'
import {
  Article,
  getPost,
  getAllArticles,
  GetAllArticlesResponse,
  RecommendedArticles,
  GetPostResponse,
} from '@/service/api/blog'
import { toStructuredData } from '@/service/api/blog/blogMapper'
import { updateAmountVisitors } from '@/service/api/blog/integration'
import { getPaths } from '@/service/api/paths'
import { Constants } from '@/utils/constants'
import { promiseSettledUtils } from '@/utils/promiseSettled'
import { withFooterHeader } from '@/utils/withFooterHeader'
import { withPageStaticProps } from '@/utils/withPageStaticProps'

type BlogPostProps = {
  data: {
    articleData: Article
    recommendedArticlesData: RecommendedArticles
  }
}

interface Params extends ParsedUrlQuery {
  id: string
}

const Container = styled(FlexContainer)`
  gap: 48px;
  background-color: white;
  padding: 0 0 112px 0;
  width: 100%;
  max-width: 100%;
`

const BlogPost: React.FC<BlogPostProps> = ({
  data: { articleData, recommendedArticlesData },
}) => {
  const updateAmountVisitorsLoadPage = useCallback(async () => {
    await updateAmountVisitors(articleData.slug)
  }, [articleData.slug])

  useEffect(() => {
    window.scrollTo(0, 0)

    setTimeout(() => {
      updateAmountVisitorsLoadPage()
    }, 5000)
  }, [updateAmountVisitorsLoadPage])

  return (
    <>
      <HeadTag
        languageOptions={[]}
        path={`/blog/${articleData.slug}`}
        title={articleData.seo?.metaTitle}
        description={articleData.seo?.metaDescription}
      />
      <ArticleJsonLd {...toStructuredData(articleData)} />
      <FlexContainer
        padding="0"
        justifyContent="center"
        style={{
          backgroundColor: 'white',
          maxWidth: '100%',
        }}
      >
        <Container direction="column">
          {articleData.src && (
            <BlogPostHeader
              imageSrc={articleData.src}
              imageAlt={articleData.alt}
              imageName={articleData.fileName}
            />
          )}
          {articleData && (
            <ContentContainer data={articleData} tags={articleData.tags} />
          )}
          {articleData.author && <AuthorContainer data={articleData.author} />}
          {articleData.about && <AboutContainer about={articleData.about} />}
          {!!recommendedArticlesData?.recommendedArticles?.length && (
            <RecommendedArticlesContainer data={recommendedArticlesData} />
          )}
        </Container>
      </FlexContainer>
    </>
  )
}

export async function getStaticPaths() {
  const { paths } = await getPaths('/articles/all')

  return {
    paths,
    fallback: 'blocking', // allow new pages to be added on runtime
  }
}

export const getStaticProps = withPageStaticProps(
  async (ctx: GetStaticPropsContext) => {
    const { params, locale } = ctx
    const { id } = params as Params
    const { getResults } = promiseSettledUtils()

    const [articleData, allArticles] = await getResults([
      getPost(id),
      getAllArticles(),
    ])

    const getArticlesByTagRelation = (props: {
      allArticles: GetAllArticlesResponse[] | null
      currentArticle: GetPostResponse | null
    }) => {
      if (!props.allArticles || !props.currentArticle) return []

      const articlesWithoutCurrentArticle = props.allArticles.filter(
        ({ article }: GetAllArticlesResponse) =>
          article.id !== props.currentArticle?.article?.id,
      )

      const relatedArticles = articlesWithoutCurrentArticle
        .filter(({ article }: GetAllArticlesResponse) =>
          article.tags.some((tag) =>
            props.currentArticle?.article?.tags.some(
              (currentTag) => currentTag === tag,
            ),
          ),
        )
        .sort(
          (firstArticle, secondArticle) =>
            secondArticle.article.tags.length -
            firstArticle.article.tags.length,
        )
        .slice(0, 2)
        .map(({ article }: GetAllArticlesResponse) => article)

      return {
        recommendedArticles: relatedArticles,
        recommendedArticlesTitle: props.currentArticle.recommendedArticlesTitle,
      } as RecommendedArticles
    }

    const recommendedArticlesData = articleData?.recommendedArticles?.length
      ? {
          recommendedArticles: articleData?.recommendedArticles,
          recommendedArticlesTitle: articleData?.recommendedArticlesTitle,
        }
      : getArticlesByTagRelation({
          allArticles,
          currentArticle: articleData,
        })

    return {
      props: {
        data: {
          articleData: articleData?.article,
          recommendedArticlesData,
        },
      },
      revalidate: Constants.ONE_DAY,
      notFound: locale === 'en',
    }
  },
)

export default withFooterHeader(BlogPost)
